import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, FileText } from 'lucide-react';
import { useUserCard } from '../hooks/useUserCard';
import SEO from '../components/SEO';

const TermsAndConditions = () => {
  const { userCard, loading } = useUserCard();

  return (
    <Layout userCard={userCard} loading={loading}>
      <SEO 
        title="Terms and Conditions - Piper Card"
        description="Read the terms and conditions for using Piper Card digital business card platform by Allgrow Technologies"
        path="/terms"
      />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Terms and Conditions</h1>
              <p className="text-gray-600">Last Updated: November 11, 2024</p>
            </div>
          </div>
        </div>

        <Card className="border-2 border-gray-100">
          <CardContent className="p-6 sm:p-8 space-y-6">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to Piper Card, a digital business card platform ("Service") operated by Allgrow Technologies 
                ("Company", "we", "us", or "our"). By accessing or using our Service, you agree to be bound by these 
                Terms and Conditions ("Terms"). If you disagree with any part of these Terms, you may not access the Service.
              </p>
            </section>

            {/* Definitions */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Definitions</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>"Service"</strong> refers to the Piper Card digital business card platform</li>
                <li><strong>"User", "you"</strong> refers to the individual accessing or using the Service</li>
                <li><strong>"Content"</strong> refers to text, images, data, or other materials uploaded by Users</li>
                <li><strong>"Account"</strong> refers to a unique account created for you to access our Service</li>
              </ul>
            </section>

            {/* User Accounts */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">3. User Accounts</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>3.1 Registration:</strong> You must register an account using Google Sign-In to use certain features of the Service.</p>
                <p><strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account and password.</p>
                <p><strong>3.3 Account Information:</strong> You agree to provide accurate, current, and complete information during registration.</p>
                <p><strong>3.4 Account Responsibility:</strong> You are responsible for all activities that occur under your account.</p>
              </div>
            </section>

            {/* Acceptable Use */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Acceptable Use Policy</h2>
              <p className="text-gray-700 mb-3">You agree NOT to use the Service to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon intellectual property rights of others</li>
                <li>Upload or transmit viruses, malware, or harmful code</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Impersonate any person or entity</li>
                <li>Collect or store personal data of other users without consent</li>
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to the Service</li>
                <li>Interfere with or disrupt the Service or servers</li>
              </ul>
            </section>

            {/* Content Ownership */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Content Ownership and Rights</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>5.1 Your Content:</strong> You retain all rights to content you upload to the Service. However, you grant us a worldwide, non-exclusive, royalty-free license to use, store, and display your content to provide the Service.</p>
                <p><strong>5.2 Our Content:</strong> The Service itself, including its design, features, and functionality, is owned by Allgrow Technologies and protected by copyright, trademark, and other laws.</p>
                <p><strong>5.3 User Content Responsibility:</strong> You are solely responsible for the content you post and the consequences of sharing it.</p>
              </div>
            </section>

            {/* User-Uploaded Content and Copyright Disclaimer */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">5A. User-Uploaded Content and Copyright Disclaimer</h2>
              <div className="space-y-3 text-gray-700 bg-red-50 border-2 border-red-200 p-4 rounded">
                <p><strong>5A.1 User-Uploaded Images, Logos, and Backgrounds:</strong> Users are responsible for all images, logos, background images, profile pictures, company logos, and any other visual content they upload to the Service.</p>
                <p><strong>5A.2 Copyright Compliance:</strong> By uploading any content to Piper Card, you represent and warrant that:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>You own all rights to the uploaded content, OR</li>
                  <li>You have obtained proper authorization, licenses, or permissions to use the content, OR</li>
                  <li>The content is in the public domain or covered under fair use</li>
                </ul>
                <p><strong>5A.3 Company Disclaimer:</strong> <span className="font-bold text-red-700">Allgrow Technologies and Piper Card are NOT responsible for any copyright infringement, intellectual property violations, or legal issues arising from user-uploaded content.</span> Users upload content at their own risk and liability.</p>
                <p><strong>5A.4 Indemnification for Copyright Issues:</strong> You agree to indemnify and hold harmless Allgrow Technologies from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from copyright infringement or intellectual property violations related to your uploaded content.</p>
                <p><strong>5A.5 Takedown Policy:</strong> If we receive a valid copyright infringement notice regarding your content, we reserve the right to remove the content immediately and may suspend or terminate your account without prior notice.</p>
                <p><strong>5A.6 User Responsibility:</strong> It is your sole responsibility to ensure that all content you upload does not violate any third-party copyrights, trademarks, or other intellectual property rights.</p>
              </div>
            </section>

            {/* Personal Information and Privacy Guidelines */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">5B. Personal Information and Privacy Guidelines</h2>
              <div className="space-y-3 text-gray-700 bg-blue-50 border-2 border-blue-200 p-4 rounded">
                <p><strong>5B.1 Business Information Only:</strong> Piper Card is designed for sharing <span className="font-bold text-blue-700">professional and business contact information only</span>. Users should limit their shared information to business-related details.</p>
                <p><strong>5B.2 Prohibited Personal Information:</strong> <span className="font-bold text-blue-700">DO NOT share sensitive Personally Identifiable Information (PII)</span> on your digital business cards or bio pages, including but not limited to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Government-issued ID numbers (SSN, Aadhaar, Passport numbers, etc.)</li>
                  <li>Full home addresses (use city/region instead)</li>
                  <li>Personal bank account or credit card information</li>
                  <li>Date of birth or age</li>
                  <li>Personal medical information</li>
                  <li>Family member information</li>
                  <li>Personal photos unrelated to business/professional context</li>
                  <li>Personal religious or political affiliations (unless relevant to business)</li>
                  <li>Any other sensitive personal data not relevant to professional networking</li>
                </ul>
                <p><strong>5B.3 Recommended Information:</strong> Appropriate information to share includes:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Professional name and title</li>
                  <li>Business email and phone number</li>
                  <li>Company name and website</li>
                  <li>Professional social media profiles (LinkedIn, Twitter, GitHub, etc.)</li>
                  <li>Professional bio and work experience</li>
                  <li>Business location (city/region)</li>
                  <li>Professional skills and services offered</li>
                </ul>
                <p><strong>5B.4 User Responsibility for Privacy:</strong> You are solely responsible for determining what information to share publicly. Once information is shared on your digital card or bio page, it becomes publicly accessible.</p>
                <p><strong>5B.5 Privacy Best Practices:</strong> We strongly recommend using business contact information rather than personal contact details. Consider using:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Business email instead of personal email</li>
                  <li>Business phone or dedicated work phone instead of personal mobile</li>
                  <li>Professional headshot instead of personal photos</li>
                  <li>Work location instead of home address</li>
                </ul>
                <p><strong>5B.6 Company Disclaimer:</strong> <span className="font-bold text-blue-700">Allgrow Technologies is not responsible for any privacy breaches, identity theft, or misuse of personal information that users choose to share on their digital business cards.</span> Users share information at their own risk.</p>
                <p><strong>5B.7 Data Security:</strong> While we implement reasonable security measures, we cannot guarantee absolute security of information you choose to make publicly available through your cards.</p>
              </div>
            </section>

            {/* Service Availability */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Service Availability</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>6.1 Availability:</strong> We strive to maintain the Service's availability but do not guarantee uninterrupted access.</p>
                <p><strong>6.2 Maintenance:</strong> We may suspend the Service for maintenance, updates, or repairs.</p>
                <p><strong>6.3 Modifications:</strong> We reserve the right to modify or discontinue the Service at any time without notice.</p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Limitation of Liability</h2>
              <div className="space-y-3 text-gray-700 bg-yellow-50 border border-yellow-200 p-4 rounded">
                <p><strong>7.1 AS-IS Basis:</strong> THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND.</p>
                <p><strong>7.2 No Warranty:</strong> We do not warrant that the Service will be error-free, secure, or free from viruses.</p>
                <p><strong>7.3 Liability Cap:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, ALLGROW TECHNOLOGIES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.</p>
                <p><strong>7.4 Maximum Liability:</strong> Our total liability shall not exceed the amount you paid us in the past 12 months, or $100, whichever is greater.</p>
              </div>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify, defend, and hold harmless Allgrow Technologies, its officers, directors, employees, 
                and agents from any claims, liabilities, damages, losses, and expenses arising from your use of the Service, 
                your violation of these Terms, or your violation of any rights of another party.
              </p>
            </section>

            {/* Data and Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Data and Privacy</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>9.1 Data Collection:</strong> We collect and process personal data as described in our Privacy Policy.</p>
                <p><strong>9.2 Data Usage:</strong> We use your data to provide and improve the Service.</p>
                <p><strong>9.3 Analytics:</strong> We collect analytics data to understand how users interact with the Service.</p>
                <p><strong>9.4 Third-Party Services:</strong> We use Firebase for authentication and may use other third-party services.</p>
              </div>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Termination</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>10.1 By You:</strong> You may terminate your account at any time by contacting us.</p>
                <p><strong>10.2 By Us:</strong> We may terminate or suspend your account immediately, without prior notice, for any reason, including breach of these Terms.</p>
                <p><strong>10.3 Effect of Termination:</strong> Upon termination, your right to use the Service will immediately cease. We may delete your content and account data.</p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Intellectual Property</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>11.1 Trademarks:</strong> "Piper Card" and all related logos are trademarks of Allgrow Technologies.</p>
                <p><strong>11.2 Copyright:</strong> All content on the Service is protected by copyright laws.</p>
                <p><strong>11.3 License:</strong> We grant you a limited, non-exclusive, non-transferable license to use the Service for personal or business purposes.</p>
              </div>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">12. Dispute Resolution</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>12.1 Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of India.</p>
                <p><strong>12.2 Jurisdiction:</strong> Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts in India.</p>
                <p><strong>12.3 Arbitration:</strong> We encourage resolution of disputes through good-faith negotiation before pursuing legal action.</p>
              </div>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">13. Changes to Terms</h2>
              <p className="text-gray-700">
                We reserve the right to modify these Terms at any time. We will notify users of any material changes by 
                updating the "Last Updated" date. Your continued use of the Service after changes constitutes acceptance 
                of the new Terms.
              </p>
            </section>

            {/* QR Code Usage */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">14. QR Code Usage</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>14.1 QR Code Generation:</strong> We provide QR code generation for your digital business cards.</p>
                <p><strong>14.2 QR Code Ownership:</strong> You may use the generated QR codes for personal or business purposes.</p>
                <p><strong>14.3 QR Code Responsibility:</strong> You are responsible for how and where you display your QR codes.</p>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">15. Contact Information</h2>
              <div className="space-y-2 text-gray-700">
                <p><strong>Company:</strong> Allgrow Technologies</p>
                <p><strong>Email:</strong> Contact@AllgrowTechnologies.com</p>
                <p><strong>Service:</strong> Piper Card Digital Business Cards</p>
              </div>
            </section>

            {/* Severability */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">16. Severability</h2>
              <p className="text-gray-700">
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited 
                or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
              </p>
            </section>

            {/* Entire Agreement */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">17. Entire Agreement</h2>
              <p className="text-gray-700">
                These Terms constitute the entire agreement between you and Allgrow Technologies regarding the use of 
                the Service and supersede all prior agreements and understandings.
              </p>
            </section>

            {/* Acceptance */}
            <section className="bg-amber-50 border border-amber-200 p-4 rounded">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Acceptance of Terms</h3>
                  <p className="text-gray-700">
                    By using Piper Card, you acknowledge that you have read, understood, and agree to be bound by these 
                    Terms and Conditions. If you do not agree, please do not use our Service.
                  </p>
                </div>
              </div>
            </section>
          </CardContent>
        </Card>

        {/* Footer Actions */}
        <div className="mt-8 text-center">
          <Link to="/">
            <Button className="bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default TermsAndConditions;
